// useActionFlowEntries.ts
import { useState, useEffect } from "react";
import {
  ActionFlowEntry,
  ActionFlowEntryDataCreate,
} from "@/modules/action-flow/model/action-flow.interface";
import ActionFlowUtils from "@/modules/action-flow/utils/ActionFlowUtils";
import DataBusDefaults from "@/services/DataBusDefaults";
import i18n from "@/i18n";
import Log from "@/debug/Log";

type Props = {
  activity: { _id: string; data: any };
  constants: { assetType: string };
};

export const useActionFlowEntries = (props: Props) => {
  const { activity, constants } = props;

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isEmpty, setIsEmpty] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [warning, setWarning] = useState<string | null>(null);
  const [offerActionFlowEntry, setOfferActionFlowEntry] =
    useState<ActionFlowEntry | null>(null);

  const fetchActionFlowEntries = async () => {
    try {
      setIsLoading(true);
      setOfferActionFlowEntry(null);
      setIsEmpty(false);

      const result = await ActionFlowUtils.getActionFlowEntriesByAsset(
        {
          assetType: constants.assetType,
          assetId: activity._id,
        },
        "active"
      );

      if (result.data.length === 0) {
        setIsEmpty(true);
      }
      if (result.data.length > 1) {
        // TODO: Replace with constant or translation if needed.
        setWarning(
          i18n.t(
            "apTemplate:Vacancy.OfferActionFlow.QR.TooMany",
            "Es wurden mehrere QR Codes gefunden, bitte wenden Sie sich an ihren Administrator."
          )
        );
      }
      setOfferActionFlowEntry(result.data[0]);
    } catch (error) {
      setError(String(error));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchActionFlowEntries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activity]);

  const createActionFlowEntry = (
    actionFlowEntry: ActionFlowEntryDataCreate,
    successMessage: string,
    errorMessage: string
  ) => {
    setIsLoading(true);

    ActionFlowUtils.createNewActionFlowEntry({
      entry: actionFlowEntry,
      successCallback: () => {
        DataBusDefaults.toast({
          type: "success",
          text: i18n.t(successMessage) || "-",
          duration: 2000,
        });
        fetchActionFlowEntries();
        setIsLoading(false);
      },
      errorCallback: () => {
        DataBusDefaults.toast({
          type: "error",
          text: i18n.t(errorMessage) || "-",
          duration: 2000,
        });
        setIsLoading(false);
      },
    });
  };

  const createActionFlowEntryAsync = async (
    actionFlowEntry: ActionFlowEntryDataCreate
  ) => {
    try {
      setIsLoading(true);

      return ActionFlowUtils.createNewActionFlowEntry({
        entry: actionFlowEntry,
      });
    } catch (error) {
      Log.error("Error creating action flow entry:", error);
      setIsLoading(false);
      throw error; // Rethrow the error for further handling
    } finally {
      setIsLoading(false);
    }
  };

  const deleteActionFlowEntry = (
    id: string,
    successMessage: string,
    errorMessage: string
  ) => {
    setIsLoading(true);
    ActionFlowUtils.archiveActionFlowEntry({
      entryId: id,
      errorCallback: () => {
        DataBusDefaults.toast({
          type: "error",
          text: i18n.t(errorMessage) || "-",
          duration: 2000,
        });
        setIsLoading(false);
      },
      successCallback: () => {
        DataBusDefaults.toast({
          type: "success",
          text: i18n.t(successMessage) || "-",
          duration: 2000,
        });
        setIsLoading(false);
        fetchActionFlowEntries();
      },
    });
  };

  return {
    isLoading,
    isEmpty,
    error,
    warning,
    offerActionFlowEntry,
    fetchActionFlowEntries,
    createActionFlowEntry,
    deleteActionFlowEntry,
    createActionFlowEntryAsync
  };
};
